import React from 'react';
import DynamicTable from '../widgets/dynamicTable/DynamicTable.jsx';
import PropTypes from 'prop-types';
import HelperMetiers360 from '../../services/HelpersMetiers360.js';
import AuthorizationChecker from '../../services/AuthorizationChecker.js';
import { useToast } from '../../hooks/useToast.jsx';
import { useModal } from '../../hooks/useModal.jsx';
import { useStoreActions, useStoreState } from 'easy-peasy';
import { Alert, Button } from 'react-bootstrap';
import ClientStatusFormComponent, { submitClientStatusForm } from './ClientStatusFormComponent.jsx';
import LogsComponent from "../logs/LogsComponent.jsx";

/**
 * 
 * @param {Array} clientStatusToDisplay array of all clientStatus to be rendered
 
 * @example
 *  <ClientStatusTableComponent clientStatusToDisplay={clientStatus} />
 * 
 */

const ClientStatusTableComponent = ({ clientStatusToDisplay = [] }) => {
    const {allClientStatus} = useStoreState(state => state.clientStatus);
    const {updateClientStatus, deleteClientStatus} = useStoreActions(actions => actions.clientStatus);
    const toast = useToast();
    const {modalComponent, setModalShow, setModalData, modalData, setIsSending} = useModal();

    const openEditModal = (clientStatus) => {
        const handleSubmit = () => {
            setIsSending(true);
            submitClientStatusForm({clientStatus, allClientStatus, action: updateClientStatus})
                .then(() => {
                    toast.success();
                    setModalShow(false);
                })
                .finally(() => setIsSending(false));
        }
        
        setModalData({
            ...modalData,
            header: <>Edition du statut client</>,
            content: <ClientStatusFormComponent clientStatus={clientStatus} />,
            cancelButton : 'Annuler',
            onValidate: handleSubmit,
            size: 'xl', 
        });
        setModalShow(true);
    }

    const openDeleteModal = (clientStatus) => {
        setModalData({
            ...modalData,
            header: <>Suppresion du statut client</>,
            content: <>
                <h5>
                    Êtes vous sûr⋅e de vouloir supprimer le statut client<strong> {clientStatus.name}</strong> &nbsp;?
                </h5>
                {clientStatus.countClients > 0 &&
                    <Alert variant='danger'>
                        <strong>Attention :</strong> Le statut sera retiré de <strong>{clientStatus.countClients}</strong> clients !
                    </Alert>}
            </>,
            resetButton: 'Supprimer',
            cancelButton: 'Annuler',
            onReset: () => {
                deleteClientStatus({uniqueId : clientStatus.uniqueId})
                    .then(() => {
                        toast.success();
                        setModalShow(false);
                    })
                    .finally(() => setIsSending(false));
            },
        });
        setModalShow(true);
    }

    const sortType = [
        {
            value: 'name', label: 'Nom',
            test:  HelperMetiers360.isArrayContainsValue(clientStatusToDisplay, "name"),
            method: (a, b) => HelperMetiers360.localeCompareWithNullable(a.name, b.name),
            display: (clientStatus) => clientStatus.name
        },
        {
            value: 'countClients', label: 'Occurence',
            test: HelperMetiers360.isArrayContainsValue(clientStatusToDisplay, "countClients"),
            method: (a, b) => HelperMetiers360.localeCompareWithNullable(a.countClients, b.countClients),
            display: (clientStatus) => clientStatus.countClients
        },
        {
            value: 'edit', label: 'Éditer',
            test: AuthorizationChecker.hasUpdateRights('clientStatus'),
            display: (clientStatus) => <Button variant="secondary" size="sm" onClick={() => {openEditModal(clientStatus)}}>
                <i className="fas fa-edit"></i>
            </Button>
        },
        {
            value: 'delete', label: 'Supprimer',
            test: AuthorizationChecker.hasDeleteRights('clientStatus'),
            display: (clientStatus) => <Button variant="danger" size="sm" onClick={() => {openDeleteModal(clientStatus)}}>
                <i className="fas fa-trash-alt"></i>
            </Button>
        },
        {
            value: 'actionLogs', label: "Logs",
            test: AuthorizationChecker.isAdmin(),
            display: (clientStatus) => <LogsComponent entityNameFromComponent="clientStatus" uniqueIdFromComponent={clientStatus.uniqueId} />,
            flatDisplay: () => null
        }
    ];

    return <>
        <DynamicTable
            contentTable={clientStatusToDisplay}
            contentSort={sortType}
            valueInitSort="name"
            index='uniqueId'
        />
        {modalComponent}
    </>
}

ClientStatusTableComponent.propTypes = {
    clientStatusToDisplay: PropTypes.array.isRequired
};
export default ClientStatusTableComponent;